/*******************************************************/
/* Angular + External */
import { BrowserModule } from "@angular/platform-browser";
import { ApplicationRef, ErrorHandler, ModuleWithProviders, NgModule } from "@angular/core";
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
// import { RouterModule, Routes } from '@angular/router';
import { FormsModule } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { FlexLayoutModule } from "@angular/flex-layout";
import { DataTableModule } from "@bfx/primeng/datatable";
import { SharedModule } from "@bfx/primeng/api";
import { TranslateModule, TranslateLoader, TranslateCompiler } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { Draggable } from "./shared/directives/draggable.directive";
import { NgResizableModule } from "@bfx/ngresizable";
import {
  TradeTableDefinitionFactory,
  OrdersTableDefinitionFactory,
  InterventionTableDefinitionFactory,
  BaseTableDefinitionFactory,
} from "shared/cubiq-services";

import { JSONPointerCompiler } from "../shared/ngx-translate";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// Golden layout
import { GoldenLayout } from "./golden-layout/golden-layout";
import { GoldenLayoutService } from "./golden-layout/golden-layout-service";
import { GoldenLayoutPopout } from "./golden-layout/golden-layout-popout";
import { GoldenLayoutPlaceholder } from "./golden-layout/golden-layout-placeholder";
import { GoldenLayoutPopupContainer } from "./golden-layout/golden-layout-popout-container";
import { GoldenLayoutItemContainerContent } from "./golden-layout/golden-layout-item-container-content";
import { GoldenLayoutNavigator } from "./golden-layout/golden-layout-navigator.component";
import { OmsPlatformOrders } from "./oms-platform-orders/oms-platform-orders";

// THIS Module
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
// AUTH
import { AuthModule } from "./bfx-auth/auth.module";
import { AuthRoutingModule } from "./bfx-auth/auth-routing.module";
// ERROR ROUTING
import { PageErrorModule } from "./bfx-page-error/page-error.module";
import { PageErrorRoutingModule } from "./bfx-page-error/page-error-routing.module";
// Bfx dialogs
import { BfxDialogsModule } from "./bfx-dialogs/bfx-dialogs.module";
// Order Audit
import { BfxOrderAuditModule } from "./bfx-order-audit/bfx-order-audit.module";
// Order Cancel
import { BfxOrderCancelModule } from "./bfx-order-cancel/bfx-order-cancel.module";
// Order Transfer
import { BfxOrderTransferModule } from "./bfx-order-transfer/bfx-order-transfer.module";
// Order Detail
import { BfxOrderDetailsModule } from "./bfx-order-details/bfx-order-details.module";
// Order Entry
import { OrderEntryModule } from "./order-entry/order-entry.module";
// Order Recap
import { BfxOrderRecapModule } from "./bfx-order-recap/bfx-order-recap.module";
// Blotter - Orders
import { BfxBlotterOrdersModule } from "./bfx-blotter-orders/bfx-blotter-orders.module";

// Ladder - Orders
import { BfxLadderOrdersModule } from "./bfx-ladder-orders/bfx-ladder-orders.module";

// Algo Monitor
import { BfxAlgoMonitorModule } from "./bfx-algo-monitor/bfx-algo-monitor.module";

// Blotter - ScratchPad
import { BfxScratchPadModule } from "./bfx-blotter-scratchpad/bfx-scratchpad.module";
// Blotter - Inbox
import { BfxBlotterInboxModule } from "./bfx-blotter-inbox/bfx-blotter-inbox.module";
// Common Module
import { BfxCommonModule } from "./bfx-common/bfx-common.module";
// Shared Module
import { BfxSharedModule } from "./bfx-shared/bfx-shared.module";
// Order Detail
import { BfxStrategyViewerModule } from "./bfx-strategy-viewer/bfx-strategy-viewer.module";
// Menus Module
import { BfxMenusModule } from "./bfx-menus/bfx-menus.module";
// User Settings Module
import { BfxUserSettingsModule } from "./bfx-user-settings/bfx-user-settings.module";
// Notifications module
// import { BfxNotificationsModule } from './bfx-notifications/bfx-notifications.module';
import { BfxApplicationNotificationModule } from "./bfx-notification-application/bfx-application-notification.module";
// Trade blotter module
import { BfxTradeBlotterModule } from "./bfx-blotter-trade/bfx-trade-blotter.module";
// Intervention blotter module
import { BfxInterventionBlotterModule } from "./bfx-blotter-intervention/bfx-intervention-blotter.module";
// Ops Int blotter module
import { BfxBlotterOpsIntModule } from "./bfx-blotter-ops-int/bfx-blotter-ops-int.module";
// BfxBenchmark Blotter Module
//import { BfxBmarkClientOrdersBlotterModule } from './bfx-bmark-summary/blotters/client-orders/bfx-bmark-client-orders-blotter.module';
// Find module
import { BfxFindModule } from "./bfx-find/bfx-find.module";
// Find Trades module
import { BfxFindDealsModule } from "./bfx-find-deals/bfx-find-deals.module";
// Find Orders module
import { BfxFindOrdersModule } from "./bfx-find-orders/bfx-find-orders.module";
// Blotter filters module
import { BfxBlotterFilterModule } from "./bfx-blotter-filter/bfx-blotter-filter.module";
// Deal Detail
import { BfxDealDetailsModule } from "./bfx-deal-details/bfx-deal-details.module";
// Contact Manager
import { BfxContactManagerModule } from "./bfx-contact-manager/bfx-contact-manager.module";
// html custom elements
import { BfxInterfaceControlsModule } from "./bfx-interface-controls/bfx-interface-controls.module";
// Detail Fill
import { BfxDetailFillModule } from "./bfx-detail-fill/bfx-detail-fill.module";
// Order PauserResume
import { BfxOrderPauseResumeModule } from "./bfx-order-pauseresume/bfx-order-pauseresume.module";
//
import { BmarkSummaryModule } from "./bfx-bmark-summary/bmark-summary.module";
// Hedge Order Monitor
import { BfxHedgeOrderMonitorModule } from "./bfx-hedge-order-monitor/bfx-hedge-order-monitor.module";

// Connection Monitor
import { BfxConnectionMonitorModule } from "./bfx-connection-monitor/bfx-connection-monitor.module"; // Connection Monitor

import { BfxATDLDesignerModule } from "./bfx-atdl-designer/bfx-atdl-designer.module";

/* TO BE MIGRATED */
// Quotes not compatiable at the moment with ag-grid structure
// import { BfxBlotterQuotesModule } from './bfx-blotter-quotes/bfx-blotter-quotes.module';
// quotes not compatible with migration to ag-grid yet
// import { BfxBlotterQuotesActionService } from './bfx-blotter-quotes/bfx-blotter-quotes-action.service';

//import { BfxBlotterOrdersActionService } from './bfx-blotter-orders/bfx-blotter-orders-action.service';

//import { BfxTradeActionsService } from './bfx-blotter-trade/services/bfx-trade-actions.service';

import { BfxOrderTakeModule } from "./bfx-order-take/bfx-order-take.module";

// perfect scrollbar include here to allow overwrite
import { PerfectScrollbarModule } from "@bfx/ngx-perfect-scrollbar";
import { PERFECT_SCROLLBAR_CONFIG } from "@bfx/ngx-perfect-scrollbar";
import { PerfectScrollbarConfigInterface } from "@bfx/ngx-perfect-scrollbar";
import { BfxPopupNotificationModule } from "./bfx-notifications/bfx-popup-notification.module";
import { LogoutNotificationService } from "./bfx-auth/logout-notification.service";
import { BfxFooterBarModule } from "./bfx-footer-bar/bfx-footer-bar.module";
import { NativeDesktopModule } from "./bfx-native-desktop/native-desktop-support.module";
import { OrderLadderDefinitionFactory } from "shared/cubiq-services/model/OrderLadderDefinitionFactory";
import { AlgoMonitorDefinitionFactory } from "shared/cubiq-services/model/AlgoMonitorDefinitionFactory";

import { AuthServicesModule } from "shared/auth-services";
import { AppErrorHandler } from "./app.errorhandler";

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

import { CubiqServicesPrimaryModule } from "shared/cubiq-services/module/CubiqServicesPrimaryModule";
import { TransferUtilitiesService } from "./bfx-blotter-orders/TransferUtilitiesService";

import { BaseComponentsModule } from "shared/base-components/base-components.module";
import { BfxBlotterOrdersActionSubjectModule } from "./bfx-blotter-orders/bfx-blotter-orders-action-subject.module";
import { BmarkSummaryActionSubjectModule } from "./bfx-bmark-summary/bmark-summary-action-subject.module";
import { BfxAppAlertContainerComponent } from "app/golden-layout/bfx-app-alert-container";
import { WindowTokenModule } from "shared/helper-services/window-token.module";
import { BfxExecutionMonitorModule } from "app/bfx-execution-monitor/execution-monitor.module";
import { BfxOrderFillNowModule } from "app/bfx-order-fill-now/bfx-order-fill-now.module";
import { BfxRiskTradeBlotterModule } from "./bfx-blotter-risk-trade/bfx-risk-trade-blotter.module";
import { WebpackTranslateLoader } from "../i18n";
import { BfxBlotterCommonModule } from "app/bfx-blotter-common/bfx-blotter-common.module";
import { NativeDesktopServicesModule } from "shared/native-desktop/NativeDesktopServicesModule";

@NgModule({
  declarations: [
    OmsPlatformOrders,
    GoldenLayout,
    GoldenLayoutPopout,
    GoldenLayoutPlaceholder,
    GoldenLayoutPopupContainer,
    GoldenLayoutItemContainerContent,
    GoldenLayoutNavigator,
    Draggable,
    AppComponent,
    BfxAppAlertContainerComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    FlexLayoutModule,
    DataTableModule,
    SharedModule,
    NgResizableModule,
    BrowserAnimationsModule,
    NativeDesktopModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader,
        deps: [HttpClient],
      },
      compiler: { provide: TranslateCompiler, useClass: JSONPointerCompiler },
    }),
    BfxCommonModule,
    BfxSharedModule,
    BfxMenusModule,
    AuthModule,
    BfxDialogsModule,
    PageErrorModule,
    PageErrorRoutingModule,
    PerfectScrollbarModule,
    BfxStrategyViewerModule,
    BfxBlotterOrdersActionSubjectModule,
    BmarkSummaryActionSubjectModule,
    BfxBlotterOrdersModule,
    BfxLadderOrdersModule,
    BfxAlgoMonitorModule,
    BfxBlotterInboxModule,
    BfxTradeBlotterModule,
    BfxRiskTradeBlotterModule,
    BfxInterventionBlotterModule,
    BfxBlotterOpsIntModule,
    BfxFindModule,
    BfxFindDealsModule,
    BfxFindOrdersModule,
    BfxScratchPadModule,
    BfxOrderAuditModule,
    BfxOrderCancelModule,
    BfxOrderTransferModule,
    BfxOrderTakeModule,
    BfxOrderFillNowModule,
    BfxOrderDetailsModule,
    OrderEntryModule,
    BfxOrderRecapModule,
    BfxApplicationNotificationModule,
    BfxUserSettingsModule,
    BfxBlotterFilterModule,
    BfxBlotterCommonModule,
    BfxInterfaceControlsModule,
    BfxDealDetailsModule,
    BfxContactManagerModule,
    BfxPopupNotificationModule,
    BfxDetailFillModule,
    BfxOrderPauseResumeModule,
    WindowTokenModule,
    AuthRoutingModule,
    BfxFooterBarModule,
    BmarkSummaryModule,
    BfxHedgeOrderMonitorModule,
    BfxConnectionMonitorModule,
    BfxATDLDesignerModule,
    AuthServicesModule,
    CubiqServicesPrimaryModule,
    NativeDesktopServicesModule,
    AppRoutingModule,
    BaseComponentsModule,
    BfxExecutionMonitorModule,
  ],
  providers: [
    AppErrorHandler,
    { provide: ErrorHandler, useClass: AppErrorHandler },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    GoldenLayoutService,
    LogoutNotificationService,
    {
      provide: BaseTableDefinitionFactory,
      useClass: OrdersTableDefinitionFactory,
    },
    TradeTableDefinitionFactory,
    InterventionTableDefinitionFactory,
    OrdersTableDefinitionFactory,
    OrderLadderDefinitionFactory,
    AlgoMonitorDefinitionFactory,
    TransferUtilitiesService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  static forRoot(): ModuleWithProviders<AppModule> {
    return {
      ngModule: AppModule,
      providers: [],
    };
  }
}
